
import { NumberBase, Constants_maxIssieBusWidth as Constants_maxIssieBusWidth_1 } from "../Common/CommonTypes.js";
import { parse as parse_1, op_UnaryNegation, tryParse, toUInt64, toUInt32_unchecked, op_BitwiseAnd, op_Division, toInt32, fromUInt32, op_Modulus, fromInt32, toString, toUInt32, equals, divRem, op_Subtraction, fromOne, op_LeftShift, op_Addition, fromZero, compare } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { ofArray, indexed, map as map_1, sum as sum_2, empty, cons, singleton, replicate as replicate_1, append, truncate, length, tail, head, isEmpty } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { toText, replace, join, replicate, interpolate, printf, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { sumBy, chunkBySize, reverse, map, toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { FSharpRef } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { Exception, equals as equals_1 } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { Bit, FastData, FastBits } from "./SimGraphTypes.js";
import { warn } from "../Shared/Log.js";
import { Result_Bind, FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { parse } from "../fable_modules/fable-library-js.5.13.0/Int32.js";

export const Constants_maxHexOnlyDisplayWidth = 16;

export const Constants_maxBinaryDisplayWidth = 32;

export const Constants_maxIssieBusWidth = Constants_maxIssieBusWidth_1;

export const Constants_maxNumericCharsBeforeTruncation = 80;

/**
 * Make negative bigints into the equivalent two's complement positive value.
 * Must be given a width.
 */
export function twosComp(width, n) {
    if (compare(n, fromZero()) < 0) {
        return op_Addition(op_LeftShift(fromOne(), width), n);
    }
    else {
        return n;
    }
}

/**
 * Make twos comp bigint n into the equivalent signed bignum value.
 * Must be given a width.
 */
export function twosCompValue(width, n) {
    if (compare(n, op_LeftShift(fromOne(), width - 1)) >= 0) {
        return op_Subtraction(n, op_LeftShift(fromOne(), width));
    }
    else {
        return n;
    }
}

function hexToBin(hStr) {
    const convert = (h) => {
        if (!isEmpty(h)) {
            const c = head(h);
            return ((c === "0") ? "0000" : ((c === "1") ? "0001" : ((c === "2") ? "0010" : ((c === "3") ? "0011" : ((c === "4") ? "0100" : ((c === "5") ? "0101" : ((c === "6") ? "0110" : ((c === "7") ? "0111" : ((c === "8") ? "1000" : ((c === "9") ? "1001" : ((c === "A") ? "1010" : ((c === "B") ? "1011" : ((c === "C") ? "1100" : ((c === "D") ? "1101" : ((c === "E") ? "1110" : ((c === "F") ? "1111" : ((c === "a") ? "1010" : ((c === "b") ? "1011" : ((c === "c") ? "1100" : ((c === "d") ? "1101" : ((c === "e") ? "1110" : ((c === "f") ? "1111" : toFail(printf("Invalid char %c while converting hex %s to binary"))(c)(hStr))))))))))))))))))))))) + convert(tail(h));
        }
        else {
            return "";
        }
    };
    const chars = toList(hStr.toLocaleLowerCase().split(""));
    if (!isEmpty(chars)) {
        const c_2 = head(chars);
        return ((c_2 === "0") ? "0" : ((c_2 === "1") ? "1" : ((c_2 === "2") ? "10" : ((c_2 === "3") ? "11" : ((c_2 === "4") ? "100" : ((c_2 === "5") ? "101" : ((c_2 === "6") ? "110" : ((c_2 === "7") ? "111" : ((c_2 === "8") ? "1000" : ((c_2 === "9") ? "1001" : ((c_2 === "A") ? "1010" : ((c_2 === "B") ? "1011" : ((c_2 === "C") ? "1100" : ((c_2 === "D") ? "1101" : ((c_2 === "E") ? "1110" : ((c_2 === "F") ? "1111" : ((c_2 === "a") ? "1010" : ((c_2 === "b") ? "1011" : ((c_2 === "c") ? "1100" : ((c_2 === "d") ? "1101" : ((c_2 === "e") ? "1110" : ((c_2 === "f") ? "1111" : toFail(printf("Invalid char %c while converting hex %s to binary"))(c_2)(hStr))))))))))))))))))))))) + convert(tail(chars));
    }
    else {
        return "";
    }
}

/**
 * Display a bignum as hex, always using 'x' prefix.
 * NB - printf %x does not work for bignums
 */
export function hexBignum(num) {
    const hexDigitsBignum = (num_1) => {
        let patternInput;
        let outArg = 0n;
        patternInput = [divRem(num_1, op_LeftShift(fromOne(), 32), new FSharpRef(() => outArg, (v) => {
            outArg = v;
        })), outArg];
        const r = patternInput[1];
        const q = patternInput[0];
        if (equals(q, fromZero())) {
            return `${interpolate("%X%P()", [toUInt32(r) >>> 0])}`;
        }
        else {
            return hexDigitsBignum(q) + (`${interpolate("%08X%P()", [toUInt32(r) >>> 0])}`);
        }
    };
    return "x" + hexDigitsBignum(num);
}

/**
 * Display a bignum as binary, always using 'b' prefix.
 */
export function binBignum(num) {
    const hex = hexBignum(num);
    return "b" + hexToBin(hex.slice(1, hex.length));
}

/**
 * Display bigint as signed twos complement value if width > 0.
 * Otherwise display bigint in signed decimal as is.
 */
export function sDecBignum(width, num) {
    if (width === 0) {
        return toString(num);
    }
    else {
        let copyOfStruct = twosCompValue(width, num);
        return toString(copyOfStruct);
    }
}

/**
 * The same as sDecBignum with width of 0.
 * Display the bignum in signed decimal as is.
 */
export function decBignum(num) {
    return toString(num);
}

function addCommasAndZeros(width, printedChars) {
    const divCeiling = (n, divisor) => (~~(((n - 1) + divisor) / divisor) | 0);
    const nonZeroDigits = printedChars.slice(1, (printedChars.length - 1) + 1);
    const numDigits = nonZeroDigits.length | 0;
    let bitsPerDigit;
    const matchValue = printedChars[0];
    bitsPerDigit = ((matchValue === "b") ? 1 : ((matchValue === "x") ? 4 : toFail(printf("Wrong use of addZeros: radix Char = %c"))(matchValue)));
    const digits = replicate(divCeiling(max(0, width - (numDigits * bitsPerDigit)), bitsPerDigit), "0") + nonZeroDigits;
    const commaSeparatedDigits = (divCeiling(digits.length, 4) === 1) ? digits : join(",", map((arg_1) => join("", reverse(arg_1)), reverse(chunkBySize(4, reverse(digits.split(""))))));
    return printedChars.slice(0, 0 + 1) + commaSeparatedDigits;
}

export const big8 = fromInt32(256);

export const big16 = fromInt32(65536);

export const big32 = op_LeftShift(fromOne(), 32);

export const big64 = op_LeftShift(fromOne(), 64);

function addZerosBignum(width, pFun, n) {
    return addCommasAndZeros(width, pFun(n));
}

/**
 * Fill a hex printed bigint with zeros to a given width in bits.
 * Add commas every 4 digits for readability.
 */
export function fillHexBignum(width, n) {
    return addZerosBignum(width, hexBignum, n);
}

/**
 * Fill a binary printed bignum with zeros to a given width.
 * Add commas every 4 bits fro readability.
 */
export function fillBinBignum(width) {
    return (n) => addZerosBignum(width, binBignum, n);
}

function printWithFill(width_mut, radix_mut, x_mut) {
    printWithFill:
    while (true) {
        const width = width_mut, radix = radix_mut, x = x_mut;
        let clo, copyOfStruct;
        if (width === 0) {
            return "0";
        }
        else if (compare(x, fromZero()) < 0) {
            return `Bignum ${x} is negative`;
        }
        else if (width < 1) {
            return `Error: ${width} is not a valid bignum width`;
        }
        else if (compare(x, op_LeftShift(fromOne(), width)) >= 0) {
            width_mut = width;
            radix_mut = radix;
            x_mut = op_Modulus(x, op_LeftShift(fromOne(), width));
            continue printWithFill;
        }
        else {
            switch (radix.tag) {
                case 1:
                    return toString(x);
                case 2:
                case 0:
                    return ((radix.tag === 2) ? (fillBinBignum) : ((radix.tag === 0) ? ((width_2) => ((n) => fillHexBignum(width_2, n))) : ((clo = toFail(printf("Can\'t happen")), (arg) => {
                        const clo_1 = clo(arg);
                        return clo_1;
                    }))))(width)(x);
                default:
                    if (compare(x, op_LeftShift(fromOne(), width - 1)) >= 0) {
                        return "-" + ((copyOfStruct = op_Subtraction(op_LeftShift(fromOne(), width), x), toString(copyOfStruct)));
                    }
                    else {
                        return toString(x);
                    }
            }
        }
        break;
    }
}

/**
 * Convert bigint to string according to radix.
 * Binary and hex numbers are zero padded to width
 * Binary is displayed as hex if width > 16
 * Single-bit binary or hex numbers do not have x or b prefix.
 * Multi-bit hex or binary numbers do.
 */
export function valToPaddedString(width, radix, value) {
    switch (radix.tag) {
        case 2:
            if (width < 2) {
                return binBignum(value).slice(1, 1 + 1);
            }
            else if (width > 16) {
                return fillHexBignum(width, value);
            }
            else {
                return fillBinBignum(width)(value);
            }
        case 0:
            if (width <= 4) {
                return hexBignum(value).slice(1, 1 + 1);
            }
            else {
                return fillHexBignum(width, value);
            }
        case 3:
            return sDecBignum(width, value);
        default:
            return decBignum(value);
    }
}

function compress(width, maxChars, radix, s) {
    let maxLength, digits_2, lastDigitIndex, find;
    const maxChars_1 = maxChars;
    let patternInput;
    const s_1 = s;
    const matchValue = s_1.length | 0;
    const matchValue_1 = s_1[0];
    let matchResult, n_1;
    switch (matchValue_1) {
        case "-": {
            matchResult = 0;
            break;
        }
        case "b": {
            matchResult = 1;
            n_1 = matchValue;
            break;
        }
        case "x": {
            matchResult = 1;
            n_1 = matchValue;
            break;
        }
        default:
            matchResult = 2;
    }
    switch (matchResult) {
        case 0: {
            patternInput = ["-", s_1.slice(1, (matchValue - 1) + 1)];
            break;
        }
        case 1: {
            patternInput = [s_1.slice(0, 0 + 1), s_1.slice(1, (n_1 - 1) + 1)];
            break;
        }
        default:
            patternInput = ["", s_1];
    }
    const pre = patternInput[0];
    const digits = patternInput[1];
    digits.length;
    const estimateCharLength = (c) => {
        switch (c) {
            case ",":
            case "1":
                return 0.75;
            default:
                return 1;
        }
    };
    const dotsLength = 3 * estimateCharLength(".");
    const preLength = equals_1(radix, NumberBase.Dec) ? 0 : 0.75;
    if ((preLength + sumBy(estimateCharLength, digits.split(""), {
        GetZero: () => 0,
        Add: (x, y) => (x + y),
    })) <= maxChars_1) {
        return pre + digits;
    }
    else {
        return (pre + "...") + ((maxLength = ((maxChars_1 - preLength) - dotsLength), (digits_2 = digits, (lastDigitIndex = ((digits_2.length - 1) | 0), (find = ((sum_mut, num_mut) => {
            find:
            while (true) {
                const sum = sum_mut, num = num_mut;
                if (num === 0) {
                    return num | 0;
                }
                else {
                    const sum_1 = sum + estimateCharLength(digits_2[num]);
                    if (sum_1 < maxLength) {
                        sum_mut = sum_1;
                        num_mut = (num - 1);
                        continue find;
                    }
                    else {
                        return num | 0;
                    }
                }
                break;
            }
        }), digits_2.slice(find(0, lastDigitIndex), lastDigitIndex + 1))))));
    }
}

/**
 * Print a FastData as a string with a given radix and width.
 * If the string is too long, truncate it.
 * MaxChars: the max printedlength of the string in char widths (emm).
 * Radix: the radix to use for the string.
 * Fd: the FastData to print.
 */
export function fastDataToPaddedString(maxChars, radix, fd) {
    const displayRadix = (radix.tag === 2) ? ((fd.Width > Constants_maxBinaryDisplayWidth) ? NumberBase.Hex : radix) : radix;
    let s;
    const matchValue = fd.Dat;
    if (matchValue.tag === 1) {
        const big = matchValue.fields[0];
        s = printWithFill(fd.Width, (displayRadix.tag === 0) ? (((fd.Width > Constants_maxHexOnlyDisplayWidth) && (decBignum(big).length < 3)) ? NumberBase.SDec : displayRadix) : displayRadix, big);
    }
    else {
        s = valToPaddedString(fd.Width, displayRadix, fromUInt32(matchValue.fields[0]));
    }
    if (s.length < maxChars) {
        return s;
    }
    else {
        return compress(fd.Width, maxChars, displayRadix, s);
    }
}

/**
 * Print a uint32 (n) as a string with a given radix and width.
 * If the string is too long, truncate it.
 * MaxChars: the max printedlength of the string in char widths (emm).
 * Radix: the radix to use for the string.
 * width: the bus width within which n should be printed.
 */
export function UInt32ToPaddedString(maxChars, radix, width, n) {
    return fastDataToPaddedString(maxChars, radix, new FastData(new FastBits(/* Word */ 0, [n]), width));
}

/**
 * Print a bigint (n) as a string with a given radix and width.
 * If the string is too long, truncate it.
 * MaxChars: the max printedlength of the string in char widths (emm).
 * Radix: the radix to use for the string.
 * width: the bus width within which n should be printed.
 */
export function BigIntToPaddedString(maxChars, radix, width, n) {
    return fastDataToPaddedString(maxChars, radix, new FastData(new FastBits(/* BigWord */ 1, [n]), width));
}

/**
 * Convert a bit to string.
 */
export function bitToString(bit) {
    if (bit.tag === 1) {
        return "1";
    }
    else {
        return "0";
    }
}

/**
 * Convert an int into a Bit list with the provided width. The Least
 * Significant Bits are the one with low index (e.g. LSB is at position 0, MSB
 * is at position N). Little Endian.
 * If the number has more bits than width, then more bits will be returned.
 */
export function convertIntToWireData(width, num) {
    const padToWidth = (width_1, bits) => {
        if (length(bits) > width_1) {
            return truncate(width_1, bits);
        }
        else {
            return append(bits, replicate_1(width_1 - length(bits), Bit.Zero));
        }
    };
    const toBit = (_arg) => {
        switch (_arg) {
            case 0:
                return Bit.Zero;
            case 1:
                return Bit.One;
            default:
                throw new Exception("toBit only accepts 0 or 1");
        }
    };
    const intToBinary = (i) => {
        if (compare(i, fromInt32(2)) < 0) {
            return singleton(toBit(~~toInt32(i)));
        }
        else {
            return cons(toBit(~~toInt32(op_Modulus(i, fromInt32(2)))), intToBinary(op_Division(i, fromInt32(2))));
        }
    };
    if (compare(num, fromZero()) >= 0) {
        return padToWidth(width, intToBinary(num));
    }
    else {
        return padToWidth(width, intToBinary(op_BitwiseAnd(num, op_Subtraction(op_LeftShift(fromOne(), width), fromOne()))));
    }
}

/**
 * Convert a list of Bits into an int. The Least Significant Bits are the one
 * with low index (e.g. LSB is at position 0, MSB is at position N).
 * Little Endian.
 */
export function convertWireDataToInt(bits) {
    const convert = (bits_1_mut, idx_mut) => {
        convert:
        while (true) {
            const bits_1 = bits_1_mut, idx = idx_mut;
            if (!isEmpty(bits_1)) {
                if (head(bits_1).tag === 1) {
                    return op_Addition(op_LeftShift(fromOne(), idx), convert(tail(bits_1), idx + 1));
                }
                else {
                    bits_1_mut = tail(bits_1);
                    idx_mut = (idx + 1);
                    continue convert;
                }
            }
            else {
                return fromZero();
            }
            break;
        }
    };
    return convert(bits, 0);
}

export function convertIntToFastData(width, n) {
    if (width <= 32) {
        return new FastData(new FastBits(/* Word */ 0, [n]), width);
    }
    else {
        return new FastData(new FastBits(/* BigWord */ 1, [fromUInt32(n)]), width);
    }
}

export function convertBigintToFastData(width, b) {
    const b_1 = twosComp(width, b);
    if (width > 32) {
        return new FastData(new FastBits(/* BigWord */ 1, [b_1]), width);
    }
    else {
        return new FastData(new FastBits(/* Word */ 0, [toUInt32(b_1) >>> 0]), width);
    }
}

export function convertBigintToUInt32(w, b) {
    const value = (w > 32) ? op_Modulus(b, op_LeftShift(fromOne(), 32)) : b;
    return toUInt32(value) >>> 0;
}

/**
 * convert to a bigint - always works. Bits < width will be correct.
 */
export function convertFastDataToBigint(d) {
    const matchValue = d.Dat;
    if (matchValue.tag === 1) {
        return matchValue.fields[0];
    }
    else {
        return fromUInt32(matchValue.fields[0]);
    }
}

/**
 * convert to int with an exception if data is too large
 */
export function convertFastDataToInt(d) {
    const matchValue = d.Dat;
    if (matchValue.tag === 1) {
        return toFail(`Can't convert ${d.Dat} to integer`);
    }
    else {
        return matchValue.fields[0];
    }
}

/**
 * Lossy conversion of bigint to int32 without exceptions
 * TODO: change this - and all dependencies with 32 bit int - to bigint?
 */
export function convertFastDataToInt32(d) {
    const matchValue = d.Dat;
    if (matchValue.tag === 1) {
        return ~~toInt32(op_BitwiseAnd(matchValue.fields[0], fromInt32(-1))) | 0;
    }
    else {
        return ~~matchValue.fields[0] | 0;
    }
}

export function convertBigintToInt32(b) {
    return ~~toInt32(op_BitwiseAnd(b, fromInt32(-1))) | 0;
}

export function convertFastDataToWireData(fastDat) {
    const matchValue = fastDat.Dat;
    if (matchValue.tag === 1) {
        const b = matchValue.fields[0];
        if (compare(b, fromZero()) < 0) {
            warn(`invalid BigWord FastData case ${b} < 0`);
            return empty();
        }
        else {
            return convertIntToWireData(fastDat.Width, b);
        }
    }
    else {
        return convertIntToWireData(fastDat.Width, fromUInt32(matchValue.fields[0]));
    }
}

export function convertWireDataToFastData(wd) {
    if (length(wd) <= 32) {
        return new FastData(new FastBits(/* Word */ 0, [toUInt32_unchecked(toUInt64(convertWireDataToInt(wd))) >>> 0]), length(wd));
    }
    else {
        return new FastData(new FastBits(/* BigWord */ 1, [sum_2(map_1((tupledArg) => {
            if (tupledArg[1].tag === 1) {
                return op_LeftShift(fromOne(), tupledArg[0]);
            }
            else {
                return fromZero();
            }
        }, indexed(wd)), {
            GetZero: () => (0n),
            Add: op_Addition,
        })]), length(wd));
    }
}

export const emptyFastData = new FastData(new FastBits(/* Word */ 0, [0]), 0);

/**
 * Try to convert a string to a bigint, or return an error message if that was
 * not possible.
 */
export function strToBigint(str) {
    const str_2 = str.toLocaleLowerCase().trim();
    let patternInput;
    let outArg = 0n;
    patternInput = [tryParse(replace((((str_2.length === 0) ? true : ((str_2.length > 1) && (str_2[0] === "x"))) ? true : (str_2[0] === "b")) ? ("0" + str_2) : str_2, ",", ""), new FSharpRef(() => outArg, (v) => {
        outArg = v;
    })), outArg];
    if (patternInput[0]) {
        return new FSharpResult$2(/* Ok */ 0, [patternInput[1]]);
    }
    else {
        return new FSharpResult$2(/* Error */ 1, ["Invalid number."]);
    }
}

/**
 * Check a number fits in <width> bits: unsigned (0 .. 2^width - 1) if it is non-negative,
 * two's complement (-2^(width-1) .. -1) if it is negative - a negative needs a sign bit,
 * so it gets one bit less of magnitude.
 * NB the negative case used to be checked by the unsigned test applied to -num-1, which
 * accepted negatives of twice the legal magnitude (-16 passed at width 4) and then
 * silently converted them to a wrong value.
 */
export function checkWidth(width, num) {
    let arg_2, arg_3;
    if ((width < 1) ? equals(num, fromZero()) : ((compare(num, fromZero()) < 0) ? (compare(num, op_UnaryNegation(op_LeftShift(fromOne(), width - 1))) >= 0) : (compare(num, op_LeftShift(fromOne(), width)) < 0))) {
        return undefined;
    }
    else if (width < 1) {
        return toText(printf("A width of %d holds no value other than 0."))(width);
    }
    else {
        return (arg_2 = op_Subtraction(op_LeftShift(fromOne(), width), fromOne()), (arg_3 = op_UnaryNegation(op_LeftShift(fromOne(), width - 1)), toText(printf("Expected a value that fits in %d bits: 0 to %O unsigned, or %O to -1 negative."))(width)(arg_2)(arg_3)));
    }
}

/**
 * Convert a string to a number making sure that it has no more bits than
 * specified in width.
 */
export function strToIntCheckWidth(width, str) {
    const matchValue = str.trim();
    if (matchValue === "") {
        return new FSharpResult$2(/* Ok */ 0, [fromZero()]);
    }
    else {
        return Result_Bind((num) => {
            const matchValue_1 = checkWidth(width, num);
            if (matchValue_1 != null) {
                return new FSharpResult$2(/* Error */ 1, [matchValue_1]);
            }
            else {
                return new FSharpResult$2(/* Ok */ 0, [num]);
            }
        }, strToBigint(matchValue));
    }
}

/**
 * Converts a binary, hex or decimal number to decimal
 * Non-standard - used by Verilog code. Should rationalise?
 */
export function toDecimal(num, numBase, width) {
    const convertBinToDec = (bits) => {
        const convert = (bits_1_mut, idx_mut) => {
            convert:
            while (true) {
                const bits_1 = bits_1_mut, idx = idx_mut;
                if (!isEmpty(bits_1)) {
                    switch (head(bits_1)) {
                        case "0": {
                            bits_1_mut = tail(bits_1);
                            idx_mut = (idx - 1);
                            continue convert;
                        }
                        case "1":
                            return op_Addition(op_LeftShift(fromOne(), idx), convert(tail(bits_1), idx - 1));
                        default:
                            return toFail(printf("Not binary input, should not happen!"));
                    }
                }
                else {
                    return fromZero();
                }
                break;
            }
        };
        return convert(ofArray(bits.split("")), bits.length - 1);
    };
    parse(width, 511, false, 32);
    switch (numBase) {
        case "\'d":
            return parse_1(num);
        case "\'b":
            return convertBinToDec(num);
        case "\'h":
            return convertBinToDec(hexToBin(num.toLocaleLowerCase()));
        default:
            return toFail(printf("Wrong base, should not happen!"));
    }
}

